import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import { formatNumber } from "../utils/format";
import ProductForm from "../components/ProductForm";
import SupplierForm from "../components/SupplierForm";
import ErrorModal from "../components/ErrorModal";
import { useErrorModal } from "../hooks/useErrorModal";

// apiFetch gooit "API-fout <status>: <raw JSON body>" - haalt hieruit het
// leesbare "detail"-veld dat de backend teruggeeft (bv. bij de 409-duplicaat-
// check), i.p.v. de rauwe JSON-tekst te tonen.
function extractErrorDetail(err) {
  const match = /"detail"\s*:\s*"([^"]+)"/.exec(err?.message || "");
  return match ? match[1] : null;
}

export default function InvoiceForm({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(id);
  // Voorstel uit de PDF-factuurherkenning (zie InvoicePdfImportPanel) - blijft
  // stabiel over re-renders omdat location.state niet verandert na de eerste mount.
  const importData = location.state?.initialData || null;
  const isPdfImport = Boolean(importData) && !isEdit;

  const [invoice, setInvoice] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [unauthorized, setUnauthorized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [selectedBrandFilter, setSelectedBrandFilter] = useState("");
  const [showValidation, setShowValidation] = useState(false);
  const [showItemValidation, setShowItemValidation] = useState(false);
  const { errorMessage, showError, clearError } = useErrorModal();

  const [formData, setFormData] = useState({
    invoice_date: new Date().toISOString().split("T")[0],
    supplier_id: "",
    invoice_number: "",
    notes: "",
  });

  const [invoiceItems, setInvoiceItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    product_id: "",
    quantity: "",
    price_excl_discount: "",
    price_incl_discount: "",
    btw_percentage: "21",
  });
  const [validationErrors, setValidationErrors] = useState({
    supplier: false,
    items: false,
    itemFields: false,
  });

  useEffect(() => {
    if (!user) return;
    fetchSuppliers();
    fetchProducts();
    fetchBrands();

    if (isEdit) {
      fetchInvoice();
    } else if (isPdfImport) {
      prefillFromImport();
    }
  }, [user, id]);

  // Voegt regels die op de factuur meermaals hetzelfde product vermelden (bv.
  // twee leveringen op één factuur) samen tot één rij: aantallen worden
  // gesommeerd, de eenheidsprijzen herberekend als gewogen gemiddelde (zelfde
  // aanpak als handleAddItem hieronder bij manuele invoer). Gegroepeerd op
  // product_id indien gematcht, anders op de (genormaliseerde) ruwe
  // omschrijving - identieke tekst op de factuur betekent hetzelfde product.
  const mergeDuplicateImportItems = (items) => {
    const groups = new Map();
    const order = [];
    items.forEach((item, idx) => {
      const key = item.product_id
        ? `product:${item.product_id}`
        : item.productNameNormalized
          ? `raw:${item.productNameNormalized.trim().toLowerCase()}`
          : `unique:${idx}`;
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, { ...item });
        order.push(key);
        return;
      }
      const existingQty = existing.quantity || 0;
      const newQty = item.quantity || 0;
      const totalQty = existingQty + newQty;
      groups.set(key, {
        ...existing,
        quantity: totalQty,
        // Prijs excl. korting = het maximum i.p.v. een gewogen gemiddelde: een
        // gratis bonusregel (vaak 0€ op de factuur, bv. "2 gratis bij 10") mag
        // de "normale" lijstprijs niet omlaag trekken - enkel de kortingprijs
        // (incl. korting) verandert daardoor.
        price_excl_discount: Math.max(existing.price_excl_discount || 0, item.price_excl_discount || 0),
        price_incl_discount: totalQty
          ? (existingQty * (existing.price_incl_discount || 0) + newQty * (item.price_incl_discount || 0)) / totalQty
          : existing.price_incl_discount,
      });
    });
    return order.map((key) => groups.get(key));
  };

  const prefillFromImport = () => {
    setFormData((prev) => ({
      ...prev,
      supplier_id: importData.supplier_id || "",
      invoice_number: importData.invoice_number_raw || "",
      invoice_date: /^\d{4}-\d{2}-\d{2}$/.test(importData.invoice_date_raw || "")
        ? importData.invoice_date_raw
        : prev.invoice_date,
    }));

    const mappedItems = (importData.items || []).map((item) => ({
      product_id: item.product_id || "",
      product_name: item.product_match_name || item.product_name_raw || "",
      quantity: item.quantity ?? "",
      price_excl_discount: item.price_excl_discount ?? "",
      price_incl_discount: item.price_incl_discount ?? "",
      rawProductName: item.product_name_raw,
      productNameNormalized: item.product_name_normalized || item.product_name_raw,
      rawBrandName: item.brand_name_raw,
      brandId: item.brand_id || null,
      btw_percentage: [0, 6, 12, 21].includes(item.btw_percentage) ? item.btw_percentage : 21,
    }));

    setInvoiceItems(mergeDuplicateImportItems(mappedItems));
  };

  const updateImportedItem = (index, field, value) => {
    setInvoiceItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Legt vast welke ruwe factuuromschrijving aan welk product gekoppeld werd,
  // zodat een volgende PDF-import met dezelfde omschrijving die koppeling
  // meteen herkent (best-effort, blokkeert de UI niet bij een fout).
  const recordProductLink = (rawDescription, productId) => {
    if (!rawDescription || !productId) return;
    apiFetch("/invoices/product-description-links", {
      method: "POST",
      body: JSON.stringify({ raw_description: rawDescription, product_id: productId }),
    }).catch((err) => console.error("Fout bij vastleggen omschrijving-koppeling:", err));
  };

  // Analoog aan recordProductLink, maar voor leveranciers - onthoudt welke
  // ruwe leveranciersnaam op de PDF aan welke bestaande leverancier gekoppeld
  // werd, zodat een volgende factuur met dezelfde (net iets afwijkend
  // geschreven) naam niet opnieuw op "Onbekend" terugvalt.
  const recordSupplierLink = (rawName, supplierId) => {
    if (!rawName || !supplierId) return;
    apiFetch("/invoices/supplier-description-links", {
      method: "POST",
      body: JSON.stringify({ raw_name: rawName, supplier_id: supplierId }),
    }).catch((err) => console.error("Fout bij vastleggen leverancier-koppeling:", err));
  };

  // Maakt het product onmiddellijk aan met de omschrijving zoals ze op de
  // factuur staat - geen tussenscherm, want de gebruiker heeft de omschrijving
  // al voor zich (in de gele melding onder de rij).
  // Merk-fallback bij onmiddellijk aanmaken: 1) merk uit de PDF-extractie zelf,
  // 2) anders de naam van de gekozen leverancier (get-or-create - in de praktijk
  // is de leverancier vaak ook meteen het merk, bv. rechtstreeks bij het merk
  // besteld), 3) pas als laatste redmiddel het generieke merk "Onbekend".
  const resolveBrandIdForQuickAdd = async (item) => {
    if (item.brandId) return item.brandId;

    const selectedSupplier = suppliers.find((s) => s.id === parseInt(formData.supplier_id, 10));
    if (selectedSupplier && selectedSupplier.name !== "Onbekend") {
      const existingBrand = brands.find((b) => b.name.toLowerCase() === selectedSupplier.name.toLowerCase());
      if (existingBrand) return existingBrand.id;
      try {
        const newBrand = await apiFetch("/brands", {
          method: "POST",
          body: JSON.stringify({ name: selectedSupplier.name }),
        });
        await fetchBrands();
        return newBrand.id;
      } catch (err) {
        console.error("Fout bij aanmaken merk uit leverancier:", err);
      }
    }

    const unknownBrand = brands.find((b) => b.name === "Onbekend");
    return unknownBrand?.id || null;
  };

  const handleQuickAddProduct = async (index) => {
    const item = invoiceItems[index];
    const brandId = await resolveBrandIdForQuickAdd(item);
    if (!brandId) {
      showError('Kon geen merk bepalen voor dit product — het merk "Onbekend" ontbreekt.');
      return;
    }
    // Genormaliseerde naam (zonder variabel volgnummer, bv. bij een magistrale
    // bereiding) i.p.v. de ruwe factuurtekst - anders krijgt elk volgnummer
    // zijn eigen, nodeloos aparte product.
    const productName = item.productNameNormalized || item.rawProductName;
    try {
      const newProduct = await apiFetch("/products", {
        method: "POST",
        body: JSON.stringify({ name: productName, brand_id: brandId }),
      });
      await fetchProducts();
      updateImportedItem(index, "product_id", newProduct.id);
      updateImportedItem(index, "product_name", newProduct.product_name || productName);
      recordProductLink(productName, newProduct.id);
    } catch (err) {
      console.error("Fout bij onmiddellijk aanmaken product:", err);
      showError(err.message || "Fout bij aanmaken product.");
    }
  };

  const fetchInvoice = () => {
    setLoadingInvoice(true);
    apiFetch(`/invoices/${id}`)
      .then((data) => {
        setInvoice(data);
        setFormData({
          invoice_date: data.invoice_date ? data.invoice_date.split("T")[0] : "",
          supplier_id: data.supplier_id,
          invoice_number: data.invoice_number || "",
          notes: data.notes || "",
        });
        setInvoiceItems(
          data.items.map((item) => ({
            product_id: item.product_id,
            product_name: item.product_name,
            sku: item.sku,
            quantity: item.quantity,
            price_excl_discount: item.price_excl_discount,
            price_incl_discount: item.price_incl_discount,
            btw_percentage: item.btw_percentage,
          }))
        );
      })
      .catch((err) => {
        console.error("Fout bij ophalen factuur:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoadingInvoice(false));
  };

  const fetchSuppliers = () => {
    apiFetch("/suppliers?sort=name&order=asc")
      .then(setSuppliers)
      .catch((err) => {
        console.error("Fout bij ophalen leveranciers:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      });
  };

  const fetchProducts = () => {
    apiFetch("/products?sort=product_name&order=asc&limit=1000")
      .then((data) => {
        // Handle new paginated response format
        if (data.items) {
          setProducts(data.items);
        } else {
          setProducts(data);
        }
      })
      .catch((err) => {
        console.error("Fout bij ophalen producten:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      });
  };

  const fetchBrands = () => {
    apiFetch("/brands?sort=name&order=asc")
      .then(setBrands)
      .catch((err) => {
        console.error("Fout bij ophalen merken:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      });
  };

  const handleAddItem = () => {
    if (!currentItem.product_id || !currentItem.quantity || !currentItem.price_excl_discount || !currentItem.price_incl_discount) {
      setShowItemValidation(true);
      setValidationErrors(prev => ({ ...prev, itemFields: true }));
      return;
    }
    setShowItemValidation(false);
    setValidationErrors(prev => ({ ...prev, itemFields: false }));

    const product = products.find((p) => p.id === parseInt(currentItem.product_id));
    const productId = parseInt(currentItem.product_id);
    const newQuantity = parseFloat(currentItem.quantity);
    const newPriceExcl = parseFloat(currentItem.price_excl_discount);
    const newPriceIncl = parseFloat(currentItem.price_incl_discount);

    // Check if product already exists in invoice items
    const existingItemIndex = invoiceItems.findIndex(item => item.product_id === productId);

    if (existingItemIndex !== -1) {
      // Product exists - merge with weighted average price
      const existingItem = invoiceItems[existingItemIndex];
      const totalQuantity = existingItem.quantity + newQuantity;

      // Calculate weighted average prices
      const weightedPriceExcl = (
        (existingItem.quantity * existingItem.price_excl_discount) +
        (newQuantity * newPriceExcl)
      ) / totalQuantity;

      const weightedPriceIncl = (
        (existingItem.quantity * existingItem.price_incl_discount) +
        (newQuantity * newPriceIncl)
      ) / totalQuantity;

      // Update existing item
      const updatedItems = [...invoiceItems];
      updatedItems[existingItemIndex] = {
        ...existingItem,
        quantity: totalQuantity,
        price_excl_discount: weightedPriceExcl,
        price_incl_discount: weightedPriceIncl,
      };
      setInvoiceItems(updatedItems);
    } else {
      // New product - add to list
      const newItem = {
        product_id: productId,
        product_name: product?.product_name || "",
        sku: product?.sku || "",
        quantity: newQuantity,
        price_excl_discount: newPriceExcl,
        price_incl_discount: newPriceIncl,
        btw_percentage: parseInt(currentItem.btw_percentage, 10),
      };
      setInvoiceItems([...invoiceItems, newItem]);
    }

    setCurrentItem({
      product_id: "",
      quantity: "",
      price_excl_discount: "",
      price_incl_discount: "",
      btw_percentage: "21",
    });
  };

  const handleRemoveItem = (index) => {
    setInvoiceItems(invoiceItems.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowValidation(true);

    const errors = {
      supplier: !formData.supplier_id,
      items: invoiceItems.length === 0,
      itemFields: false,
    };

    setValidationErrors(errors);

    if (errors.supplier || errors.items) {
      return;
    }

    if (invoiceItems.some((item) => !item.product_id)) {
      showError("Koppel elke productlijn aan een bestaand of nieuw product vóór het opslaan.");
      return;
    }

    setLoading(true);

    const payload = {
      invoice_date: formData.invoice_date,
      supplier_id: parseInt(formData.supplier_id),
      invoice_number: formData.invoice_number || null,
      notes: formData.notes || null,
      items: invoiceItems.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        price_excl_discount: item.price_excl_discount,
        price_incl_discount: item.price_incl_discount,
        btw_percentage: item.btw_percentage,
      })),
    };

    const method = isEdit ? "PATCH" : "POST";
    const url = isEdit ? `/invoices/${id}` : "/invoices";

    apiFetch(url, {
      method,
      body: JSON.stringify(payload),
    })
      .then(() => {
        navigate("/purchases");
      })
      .catch((err) => {
        console.error("Fout bij opslaan factuur:", err);
        showError(extractErrorDetail(err) || "Er is een fout opgetreden bij het opslaan van de factuur.");
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoading(false));
  };

  const handleCreateSupplier = async (supplierData) => {
    const newSupplier = await apiFetch("/suppliers", {
      method: "POST",
      body: JSON.stringify(supplierData),
    });

    await fetchSuppliers();
    setShowAddSupplierModal(false);
    setFormData((prev) => ({ ...prev, supplier_id: newSupplier.id }));
    setValidationErrors((prev) => ({ ...prev, supplier: false }));

    if (isPdfImport && importData.supplier_name_raw) {
      recordSupplierLink(importData.supplier_name_raw, newSupplier.id);
    }

    return newSupplier;
  };

  const handleCreateProduct = async (productData) => {
    try {
      const newProduct = await apiFetch("/products", {
        method: "POST",
        body: JSON.stringify(productData),
      });

      // Refresh products list
      await fetchProducts();

      // Close modal
      setShowAddProductModal(false);

      // Auto-select the new product
      setCurrentItem({ ...currentItem, product_id: newProduct.id });

      return newProduct;
    } catch (err) {
      console.error("Fout bij aanmaken product:", err);
      throw err;
    }
  };

  // "Bedrag excl. BTW" van een lijn = aantal x prijs incl. korting (de netto
  // aankoopkost na korting, vóór BTW). BTW% is een aparte, categorische
  // eigenschap van de lijn (0/6/21) - geen bedrag om te wegen bij samenvoegen.
  const getLineExclBtw = (item) => (item.quantity || 0) * (item.price_incl_discount || 0);
  const getLineBtwAmount = (item) => getLineExclBtw(item) * ((item.btw_percentage ?? 21) / 100);
  const getLineInclBtw = (item) => getLineExclBtw(item) + getLineBtwAmount(item);

  const getTotalExclBtw = () => invoiceItems.reduce((sum, item) => sum + getLineExclBtw(item), 0);
  const getTotalBtwAmount = () => invoiceItems.reduce((sum, item) => sum + getLineBtwAmount(item), 0);
  const getTotalInclBtw = () => getTotalExclBtw() + getTotalBtwAmount();

  // Indicatief kortingspercentage: hoeveel de prijs incl. korting lager ligt
  // dan de lijstprijs (excl. korting). Puur informatief, niet opgeslagen.
  const getLineDiscountPct = (item) => {
    const excl = item.price_excl_discount || 0;
    if (!excl) return 0;
    return (1 - (item.price_incl_discount || 0) / excl) * 100;
  };
  const getTotalListPrice = () => invoiceItems.reduce((sum, item) => sum + (item.quantity || 0) * (item.price_excl_discount || 0), 0);
  const getTotalDiscountPct = () => {
    const listTotal = getTotalListPrice();
    if (!listTotal) return 0;
    return (1 - getTotalExclBtw() / listTotal) * 100;
  };

  const handleBrandFilterChange = (brandId) => {
    setSelectedBrandFilter(brandId);
  };

  const filteredProducts = selectedBrandFilter
    ? products.filter(product => product.brand_id === parseInt(selectedBrandFilter))
    : products;

  if (unauthorized) return <Navigate to="/unauthorized" replace />;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{isEdit ? "Factuur Bewerken" : "Nieuwe Factuur"}</h1>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Invoice Header */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Factuurgegevens</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Factuurdatum *
              </label>
              <input
                type="date"
                required
                value={formData.invoice_date}
                onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Leverancier *
              </label>
              <div className="flex gap-2">
                <select
                  required
                  value={formData.supplier_id}
                  onChange={(e) => {
                    setFormData({ ...formData, supplier_id: e.target.value });
                    if (e.target.value) setValidationErrors(prev => ({ ...prev, supplier: false }));
                    if (isPdfImport && importData.supplier_name_raw && e.target.value) {
                      recordSupplierLink(importData.supplier_name_raw, parseInt(e.target.value, 10));
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded transition-colors ${
                    validationErrors.supplier || (showValidation && !formData.supplier_id)
                      ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                      : "border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  }`}
                >
                  <option value="">-- Selecteer leverancier --</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowAddSupplierModal(true)}
                  className="px-3 bg-gray-600 text-white rounded hover:bg-gray-700"
                  title="Nieuwe leverancier aanmaken"
                >
                  +
                </button>
              </div>
              {validationErrors.supplier && (
                <p className="mt-1 text-sm text-red-600">Selecteer een leverancier</p>
              )}
              {isPdfImport && importData.supplier_matched === false && (
                <p className="mt-1 text-xs text-amber-700">
                  Leverancier op de PDF: "{importData.supplier_name_raw}" — niet automatisch
                  herkend, nu op "Onbekend" gezet. Corrigeer hierboven indien nodig.
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Factuurnummer
              </label>
              <input
                type="text"
                value={formData.invoice_number}
                onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded"
                placeholder="bv. 235502-2026000051"
              />
              <p className="mt-1 text-xs text-gray-500">
                Samen met leverancier en datum gebruikt om dubbele import te detecteren.
              </p>
            </div>
            {isPdfImport && importData.possible_duplicate && (
              <div className="md:col-span-2 p-3 bg-red-50 border border-red-300 rounded text-sm text-red-800">
                ⚠️ Deze factuur (nr. {importData.possible_duplicate.invoice_number}) van{" "}
                <strong>{importData.possible_duplicate.supplier_name}</strong> op{" "}
                {importData.possible_duplicate.invoice_date} lijkt al eerder geïmporteerd te zijn
                (factuur #{importData.possible_duplicate.invoice_id}). Controleer of dit geen
                dubbele import is vóór je opslaat.
              </div>
            )}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Opmerkingen
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                rows="2"
              />
            </div>
          </div>
        </div>

        {/* Invoice Items */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Productlijnen</h2>

          {/* Add Item Form */}
          <div className="mb-4 p-4 bg-gray-50 rounded border">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Nieuw product toevoegen</h3>
            <div className="flex gap-2">
              <div className="w-48">
                <label className="block text-xs font-medium text-gray-600 mb-1">Merk</label>
                <select
                  value={selectedBrandFilter}
                  onChange={(e) => handleBrandFilterChange(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="">Alle merken</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Product</label>
                <select
                  value={currentItem.product_id}
                  onChange={(e) => setCurrentItem({ ...currentItem, product_id: e.target.value })}
                  className={`w-full px-3 py-2 border rounded text-sm transition-colors ${
                    showItemValidation && !currentItem.product_id
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300"
                  }`}
                >
                  <option value="">-- Selecteer product --</option>
                  {filteredProducts.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.product_name} {product.sku ? `(${product.sku})` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-12 flex items-end">
                <button
                  type="button"
                  onClick={() => setShowAddProductModal(true)}
                  className="w-full bg-gray-600 text-white rounded hover:bg-gray-700 text-sm flex items-center justify-center"
                  style={{ height: '38px' }}
                  title="Nieuw product aanmaken"
                >
                  +
                </button>
              </div>
              <div className="w-24">
                <label className="block text-xs font-medium text-gray-600 mb-1">Aantal</label>
                <input
                  type="number"
                  step="1"
                  value={currentItem.quantity}
                  onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                  className={`w-full px-3 py-2 border rounded text-sm transition-colors ${
                    showItemValidation && !currentItem.quantity
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300"
                  }`}
                  placeholder="0"
                />
              </div>
              <div className="w-32">
                <label className="block text-xs font-medium text-gray-600 mb-1">Prijs excl. korting</label>
                <input
                  type="number"
                  step="0.01"
                  value={currentItem.price_excl_discount}
                  onChange={(e) => setCurrentItem({ ...currentItem, price_excl_discount: e.target.value })}
                  className={`w-full px-3 py-2 border rounded text-sm transition-colors ${
                    showItemValidation && !currentItem.price_excl_discount
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300"
                  }`}
                  placeholder="0.00"
                />
              </div>
              <div className="w-32">
                <label className="block text-xs font-medium text-gray-600 mb-1">Prijs incl. korting</label>
                <input
                  type="number"
                  step="0.01"
                  value={currentItem.price_incl_discount}
                  onChange={(e) => setCurrentItem({ ...currentItem, price_incl_discount: e.target.value })}
                  className={`w-full px-3 py-2 border rounded text-sm transition-colors ${
                    showItemValidation && !currentItem.price_incl_discount
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300"
                  }`}
                  placeholder="0.00"
                />
              </div>
              <div className="w-24">
                <label className="block text-xs font-medium text-gray-600 mb-1">BTW%</label>
                <select
                  value={currentItem.btw_percentage}
                  onChange={(e) => setCurrentItem({ ...currentItem, btw_percentage: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                >
                  <option value="0">0%</option>
                  <option value="6">6%</option>
                  <option value="12">12%</option>
                  <option value="21">21%</option>
                </select>
              </div>
              <div className="w-32 flex items-end">
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm whitespace-nowrap"
                >
                  Toevoegen
                </button>
              </div>
            </div>
            {validationErrors.itemFields && (
              <p className="mt-2 text-sm text-red-600">Vul alle velden in voor het product</p>
            )}
          </div>

          {isPdfImport && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
              Voorstel op basis van de geüploade PDF. Regels met een gele rand konden niet
              automatisch aan een bestaand product gekoppeld worden — kies het juiste product uit
              de lijst, of maak het aan via "+". Controleer ook de aantallen en prijzen.
              {importData.discount_rate_applied != null && (
                <>
                  {" "}Deze factuur toont enkel de lijstprijs per lijn en past de korting toe als
                  één percentage op het totaal — een globale korting van{" "}
                  <strong>{formatNumber(importData.discount_rate_applied)}%</strong> (uit het
                  totalenblok van de factuur) werd daarom proportioneel over elke lijn verdeeld.
                  Controleer dit tegen de originele factuur.
                </>
              )}
            </div>
          )}

          {/* Validation error for empty items */}
          {validationErrors.items && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-600">Voeg minstens één product toe aan de factuur.</p>
            </div>
          )}

          {/* Loading Indicator */}
          {loadingInvoice && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Factuurgegevens laden...</span>
            </div>
          )}

          {/* Items Table */}
          {!loadingInvoice && invoiceItems.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Product</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-700">Aantal</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-700">Prijs excl. korting</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-700">Prijs incl. korting</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-700">Korting%</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-700">BTW%</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-700">Bedrag excl. BTW</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-700">BTW</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-700">Bedrag incl. BTW</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-700">Acties</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoiceItems.map((item, index) =>
                    isPdfImport ? (
                      <tr key={index} className={!item.product_id ? "bg-amber-50" : ""}>
                        <td className="px-4 py-2">
                          {!item.product_id && item.rawProductName && (
                            <p className="mb-1 text-xs text-amber-700">Op factuur: "{item.rawProductName}"{item.rawBrandName ? ` (${item.rawBrandName})` : ""}</p>
                          )}
                          <div className="flex gap-1 items-center">
                            <select
                              value={item.product_id}
                              onChange={(e) => {
                                const productId = parseInt(e.target.value);
                                const product = products.find((p) => p.id === productId);
                                updateImportedItem(index, "product_id", productId);
                                updateImportedItem(index, "product_name", product?.product_name || "");
                                recordProductLink(item.productNameNormalized || item.rawProductName, productId);
                              }}
                              className={`w-full px-2 py-1.5 border rounded text-sm ${
                                !item.product_id ? "border-amber-400" : "border-gray-300"
                              }`}
                            >
                              <option value="">-- Selecteer product --</option>
                              {products.map((product) => (
                                <option key={product.id} value={product.id}>
                                  {product.product_name} {product.sku ? `(${product.sku})` : ""}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => handleQuickAddProduct(index)}
                              className="px-2 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
                              title="Product onmiddellijk aanmaken met de omschrijving van de factuur"
                            >
                              +
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <input
                            type="number"
                            step="1"
                            value={item.quantity}
                            onChange={(e) => updateImportedItem(index, "quantity", parseInt(e.target.value, 10) || 0)}
                            className="w-20 px-2 py-1.5 border border-gray-300 rounded text-sm text-right"
                          />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <input
                            type="number"
                            step="0.01"
                            value={item.price_excl_discount}
                            onChange={(e) => updateImportedItem(index, "price_excl_discount", parseFloat(e.target.value) || 0)}
                            className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm text-right"
                          />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <input
                            type="number"
                            step="0.01"
                            value={item.price_incl_discount}
                            onChange={(e) => updateImportedItem(index, "price_incl_discount", parseFloat(e.target.value) || 0)}
                            className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm text-right"
                          />
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-500">
                          {formatNumber(getLineDiscountPct(item))}%
                        </td>
                        <td className="px-4 py-2 text-right">
                          <select
                            value={item.btw_percentage}
                            onChange={(e) => updateImportedItem(index, "btw_percentage", parseInt(e.target.value, 10))}
                            className="px-2 py-1.5 border border-gray-300 rounded text-sm"
                          >
                            <option value="0">0%</option>
                            <option value="6">6%</option>
                            <option value="21">21%</option>
                          </select>
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-900">
                          €{formatNumber(getLineExclBtw(item))}
                        </td>
                        <td className="px-4 py-2 text-sm text-right text-gray-900">
                          €{formatNumber(getLineBtwAmount(item))}
                        </td>
                        <td className="px-4 py-2 text-sm text-right font-medium text-gray-900">
                          €{formatNumber(getLineInclBtw(item))}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className="text-red-600 hover:text-red-900 text-sm"
                          >
                            Verwijderen
                          </button>
                        </td>
                      </tr>
                    ) : (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/products/${item.product_id}`)}
                      >
                        <td className="px-4 py-2 text-sm text-gray-900">{item.product_name}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(item.quantity, 0)}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-900">€{formatNumber(item.price_excl_discount)}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-900">€{formatNumber(item.price_incl_discount)}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatNumber(getLineDiscountPct(item))}%</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(item.btw_percentage, 0)}%</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-900">€{formatNumber(getLineExclBtw(item))}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-900">€{formatNumber(getLineBtwAmount(item))}</td>
                        <td className="px-4 py-2 text-sm text-right font-medium text-gray-900">
                          €{formatNumber(getLineInclBtw(item))}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveItem(index);
                            }}
                            className="text-red-600 hover:text-red-900 text-sm"
                          >
                            Verwijderen
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                  <tr className="bg-gray-50 font-bold">
                    <td colSpan="6" className="px-4 py-3 text-right text-sm text-gray-900">
                      Totaal (gem. korting {formatNumber(getTotalDiscountPct())}%):
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">
                      €{formatNumber(getTotalExclBtw())}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">
                      €{formatNumber(getTotalBtwAmount())}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">
                      €{formatNumber(getTotalInclBtw())}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/purchases")}
            className="px-6 py-2 border rounded hover:bg-gray-100"
            disabled={loading}
          >
            Annuleren
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
            disabled={loading}
          >
            {loading ? "Bezig..." : isEdit ? "Bijwerken" : "Opslaan"}
          </button>
        </div>
      </form>

      {/* Error Modal */}
      <ErrorModal message={errorMessage} onClose={clearError} />

      {/* Add Supplier Modal */}
      {showAddSupplierModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Nieuwe Leverancier Toevoegen</h2>
              <button
                onClick={() => setShowAddSupplierModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <SupplierForm
              onSubmit={handleCreateSupplier}
              initialValues={
                isPdfImport
                  ? {
                      name: importData.supplier_name_raw || "",
                      contact_person: importData.supplier_contact_person_raw || "",
                      email: importData.supplier_email_raw || "",
                      phone: importData.supplier_phone_raw || "",
                    }
                  : undefined
              }
            />
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Nieuw Product Toevoegen</h2>
              <button
                onClick={() => setShowAddProductModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <ProductForm onSubmit={handleCreateProduct} />
          </div>
        </div>
      )}
    </div>
  );
}
